"use client";

import { useState, useEffect } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { toast } from "react-toastify";
import { useTranslations } from "next-intl";
import { assignCourse } from "@/helpers/api/teams";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";

export function AssignCourseModal({
    isOpen,
    onClose,
    teamId,
    members = [],
    onSuccess,
}) {
    const t = useTranslations("teamsAssign");
    const [selectedMember, setSelectedMember] = useState("");
    const [selectedCourse, setSelectedCourse] = useState("");
    const [dueDate, setDueDate] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingCourses, setLoadingCourses] = useState(false);
    const [courses, setCourses] = useState([]);
    const [error, setError] = useState(null);
    const [searchMember, setSearchMember] = useState("");
    const [searchCourse, setSearchCourse] = useState("");

    // Charger les formations disponibles
    useEffect(() => {
        if (isOpen) {
            fetchCourses();
        }
    }, [isOpen]);

    const fetchCourses = async () => {
        setLoadingCourses(true);
        try {
            const response = await fetch("/api/teams/courses", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
            });
            const data = await response.json();
            if (data.courses) {
                setCourses(data.courses);
            }
        } catch (err) {
            console.error("Erreur lors du chargement des formations:", err);
        } finally {
            setLoadingCourses(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!selectedMember) {
            setError(t("errorMemberRequired"));
            return;
        }

        if (!selectedCourse) {
            setError(t("errorCourseRequired"));
            return;
        }

        setLoading(true);

        try {
            const result = await assignCourse(
                teamId,
                selectedMember,
                selectedCourse,
                dueDate || null
            );

            const memberName = members.find(m => m.userId?.toString() === selectedMember)?.firstName || "";
            const courseName = courses.find(c => c.id?.toString() === selectedCourse)?.title || "";

            toast.success(t("successAssigned", { member: memberName, course: courseName }));
            resetForm();
            onSuccess?.(result);
            onClose();
        } catch (err) {
            console.error("Error:", err);
            setError(err.message || t("errorAssignFailed"));
            toast.error(err.message || t("errorAssignFailed"));
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setSelectedMember("");
        setSelectedCourse("");
        setDueDate("");
        setError(null);
        setSearchMember("");
        setSearchCourse("");
    };

    const handleClose = () => {
        if (!loading) {
            resetForm();
            onClose();
        }
    };

    // Filtrer les membres actifs
    const activeMembers = members.filter(
        (m) => m.status === "active" || !m.status
    );

    // Filtrer les membres par recherche
    const filteredMembers = activeMembers.filter((member) => {
        const fullName = `${member.firstName || ""} ${member.lastName || ""}`.toLowerCase();
        return fullName.includes(searchMember.toLowerCase());
    });

    // Filtrer les formations par recherche
    const filteredCourses = courses.filter((course) => {
        return course.title?.toLowerCase().includes(searchCourse.toLowerCase());
    });

    // Date minimum (aujourd'hui)
    const today = new Date().toISOString().split("T")[0];

    return (
        <BottomSheetModal
            isOpen={isOpen}
            onClose={handleClose}
            maxWidthClass="sm:max-w-md"
            zIndexClass="z-[100]"
        >
            <div className="flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-grayDark-700">
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-grayDark-100 tracking-tight">
                            {t("title")}
                        </h2>
                        <button
                            onClick={handleClose}
                            disabled={loading}
                            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-grayDark-300 hover:bg-gray-100 dark:hover:bg-grayDark-700 rounded-lg transition-colors disabled:opacity-50"
                        >
                            <SolarIcon name="close-circle-linear" className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
                        {/* Sélection du membre */}
                        <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-grayDark-100 mb-2">
                                <SolarIcon name="user-rounded-linear" className="w-4 h-4 inline-block mr-2 text-gray-500" />
                                {t("member")}
                            </label>
                            <div className="relative mb-2">
                                <SolarIcon name="magnifer-linear" className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    value={searchMember}
                                    onChange={(e) => setSearchMember(e.target.value)}
                                    placeholder={t("searchMember")}
                                    className="w-full pl-9 pr-4 py-2 bg-white dark:bg-grayDark-700 border border-gray-200 dark:border-grayDark-600 rounded-xl text-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                                    disabled={loading}
                                />
                            </div>
                            <select
                                value={selectedMember}
                                onChange={(e) => {
                                    setSelectedMember(e.target.value);
                                    setError(null);
                                }}
                                className={`w-full px-4 py-2.5 bg-white dark:bg-grayDark-700 border rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm ${
                                    error && !selectedMember
                                        ? "border-red-500"
                                        : "border-gray-200 dark:border-grayDark-600"
                                }`}
                                disabled={loading}
                            >
                                <option value="">{t("selectMember")}</option>
                                {filteredMembers.map((member, index) => (
                                    <option
                                        key={`member-${member.userId || index}`}
                                        value={member.userId}
                                    >
                                        {member.firstName} {member.lastName}
                                    </option>
                                ))}
                            </select>
                            {activeMembers.length === 0 && (
                                <p className="text-xs text-gray-500 dark:text-grayDark-400 mt-2 mb-0">
                                    {t("noActiveMembers")}
                                </p>
                            )}
                        </div>

                        {/* Sélection de la formation */}
                        <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-grayDark-100 mb-2">
                                <SolarIcon name="book-2-linear" className="w-4 h-4 inline-block mr-2 text-gray-500" />
                                {t("course")}
                            </label>
                            <div className="relative mb-2">
                                <SolarIcon name="magnifer-linear" className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    value={searchCourse}
                                    onChange={(e) => setSearchCourse(e.target.value)}
                                    placeholder={t("searchCourse")}
                                    className="w-full pl-9 pr-4 py-2 bg-white dark:bg-grayDark-700 border border-gray-200 dark:border-grayDark-600 rounded-xl text-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                                    disabled={loading || loadingCourses}
                                />
                            </div>
                            {loadingCourses ? (
                                <div className="flex items-center justify-center py-4">
                                    <SolarIcon name="refresh-linear" className="w-5 h-5 animate-spin text-blue-600" />
                                </div>
                            ) : (
                                <select
                                    value={selectedCourse}
                                    onChange={(e) => {
                                        setSelectedCourse(e.target.value);
                                        setError(null);
                                    }}
                                    className={`w-full px-4 py-2.5 bg-white dark:bg-grayDark-700 border rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm ${
                                        error && !selectedCourse
                                            ? "border-red-500"
                                            : "border-gray-200 dark:border-grayDark-600"
                                    }`}
                                    disabled={loading}
                                >
                                    <option value="">{t("selectCourse")}</option>
                                    {filteredCourses.map((course, index) => (
                                        <option key={`course-${course.id || index}`} value={course.id}>
                                            {course.title}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        {/* Date d'échéance (optionnel) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-grayDark-100 mb-2">
                                <SolarIcon name="calendar-linear" className="w-4 h-4 inline-block mr-2 text-gray-500" />
                                {t("dueDate")}
                                <span className="text-gray-400 dark:text-grayDark-500 font-normal ml-1">
                                    ({t("optional")})
                                </span>
                            </label>
                            <input
                                type="date"
                                value={dueDate}
                                onChange={(e) => setDueDate(e.target.value)}
                                min={today}
                                className="w-full px-4 py-2.5 bg-white dark:bg-grayDark-700 border border-gray-200 dark:border-grayDark-600 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                                disabled={loading}
                            />
                            <p className="text-xs text-gray-500 dark:text-grayDark-400 mt-2 mb-0">
                                {t("dueDateDescription")}
                            </p>
                        </div>

                        {/* Message d'erreur */}
                        {error && (
                            <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm p-3 rounded-xl">
                                {error}
                            </div>
                        )}

                        {/* Info Box */}
                        <div className="bg-gray-50 dark:bg-grayDark-700/50 rounded-xl p-4">
                            <p className="text-sm text-gray-600 dark:text-grayDark-300 mb-0">
                                {t("infoBox")}
                            </p>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 pt-3">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={loading}
                                className="flex-1 px-4 py-2.5 text-blue-600 dark:text-blue-400 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-grayDark-700 transition-colors disabled:opacity-50"
                            >
                                {t("cancel")}
                            </button>
                            <button
                                type="submit"
                                disabled={loading || loadingCourses}
                                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <SolarIcon name="refresh-linear" className="w-4 h-4 animate-spin" />
                                        <span>{t("assigning")}</span>
                                    </>
                                ) : (
                                    <span>{t("assign")}</span>
                                )}
                            </button>
                        </div>
                    </form>
            </div>
        </BottomSheetModal>
    );
}
